/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';
import { Link, LinkProps, useLocation } from 'react-router-dom';
import cx from 'classnames';
import { createPath } from 'history';
import { get } from 'lodash';
import { mustHaveMetadataProps } from '../../../oms/components/AuroraOrderView/components/AuroraErrorColumn/AuroraErrorColumn';
import { useRouteLink } from '@broadleaf/admin-components/dist/metadata';
import Preloader from '@broadleaf/admin-components/dist/common/helpers/Preloader';
import { SimpleIcon } from '@broadleaf/admin-components/dist/common';
import { LinkColumnProps } from '@broadleaf/admin-components/dist/form/named-components/column-types/LinkColumn';
import { unstable } from '@broadleaf/admin-components';
const { metadataInvariant } = unstable;

/*import Link, { LinkProps } from 'common/elements/Link';
import { SimpleIcon } from 'common/elements/SimpleIcon';
import Preloader from 'common/helpers/Preloader';
import { mustHaveMetadataProps } from 'form/utils/invariantHelpers';
import useRouteLink from 'metadata/hooks/useRouteLink';
import metadataInvariant from 'metadata/utils/MetadataInvariant';
import type { ICommonGridColumn } from 'types/common';*/

/**
 * Renders a column with a link. The link contains a back-reference to the previous location.
 *
 * @visibleName LINK
 */
export const ExternalLinkColumn: React.FC<LinkColumnProps> = ({
  header,
  row
}) => {
  const from = useLocation();
  mustHaveMetadataProps(header, ['name']);

  const title = get(row, header.name);
  const link = get(header, 'attributes.link');
  const truncate = get(header, 'attributes.truncate', true);
  const newTab = get(header, 'attributes.newTab', false);

  metadataInvariant(
    !!link,
    'LINK column types must be provided `attributes.link`.',
    header
  );

  const isExternalLink = get(header, 'attributes.externalLink', false);
  if (isExternalLink) {
    const externalLinkProp = get(header, 'attributes.externalLinkProp', null);
    const externalLink = externalLinkProp ? get(row, externalLinkProp) : null;
    const externalLinkTitle = get(
      header,
      'attributes.externalLinkTitle',
      'Open'
    );
    const externalLinkProps = {
      rel: 'noopener noreferrer',
      target: '_blank',
      href: externalLink
    };
    return (
      <div className={cx({ 'tw-truncate': truncate })}>
        {externalLinkProps.href ? (
          <a
            className="tw-flex tw-items-center tw-font-semibold tw-text-link-600 hover:tw-text-link-700"
            {...externalLinkProps}
          >
            <span>{externalLinkTitle}</span>
            <SimpleIcon className="tw-ml-2" iconName="external-link-alt" />
          </a>
        ) : (
          <span title={title}>{title}</span>
        )}
      </div>
    );
  }
  const { location: to, getPreloaderProps } = useRouteLink({
    // @ts-ignore
    from,
    link,
    vars: row
  });

  const linkProps: LinkProps = { to, title };
  if (newTab) {
    const href = typeof to === 'string' ? to : createPath(to);

    linkProps.target = '_blank';
    linkProps.rel = href.startsWith('/') ? undefined : 'noopener noreferrer';
  }

  // if we provided a "link" attribute, use that to link to an existing route
  return (
    <Preloader {...getPreloaderProps()} className="LinkColumn">
      <div className={cx({ 'tw-truncate': truncate })}>
        {to ? (
          <Link
            className="tw-flex tw-items-center tw-font-semibold tw-text-link-600 hover:tw-text-link-700"
            {...linkProps}
          >
            <span>{title}</span>
            {newTab && (
              <SimpleIcon className="tw-ml-2" iconName="external-link-alt" />
            )}
          </Link>
        ) : (
          <span title={title}>{title}</span>
        )}
      </div>
    </Preloader>
  );
};

/**
 | Property | Description |
 | ---- | ---- |
 | `name` | the name of this column in the row data (passed as result prop) |
 | `attributes.link` | the link to be redirected to. You can insert values from the row by using `:` as a prefix character
 For example, `/products/:id` would indicate we should replace `:id` with the value of `row.id`.|
 */

export default ExternalLinkColumn;
